"use client";

import { useEffect, useState } from "react";
import { editionFromParams } from "@/lib/onboarding/edition-href";

/**
 * Keep a visitor inside the edition they arrived on when they follow a link the
 * funnel itself offers them.
 *
 * Every one of those links was hard-coded to the house path: "Start over" after
 * an error went to `/onboarding`, and three "Sign in" links went to `/login`
 * (one of them with `callbackUrl=%2Fonboarding`, so both the login page AND the
 * place it returned them to were ours). Someone who came in through a
 * reseller's door was quietly standing in the platform's, told nothing — and
 * the error case is the worst of them, because it lands exactly when a visitor
 * is already deciding whether this company is competent.
 *
 * Read from the LIVE URL rather than threaded down as a prop because one of the
 * four call sites is `(onboarding)/error.tsx`, a React error boundary, which
 * receives no props of its own and no searchParams. One mechanism for all four
 * beats a prop chain that stops one component short of the place it is most
 * needed.
 *
 * In an effect, not during render: reading `window` while rendering would make
 * the server's HTML and the client's first pass disagree about an href. These
 * are links, not redirects — the value is in place long before anything can be
 * clicked.
 */
export function useEditionSlug(): string | null {
  const [edition, setEdition] = useState<string | null>(null);
  useEffect(() => {
    setEdition(editionFromParams(new URLSearchParams(window.location.search)));
  }, []);
  return edition;
}

export {
  editionFromParams,
  forgotPasswordHref,
  loginHref,
  onboardingHref,
  signInHref,
} from "@/lib/onboarding/edition-href";
